import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { PlusIcon, TrashIcon, CheckCircleIcon, XMarkIcon, SparklesIcon, ArrowDownOnSquareIcon, ArrowLeftIcon, ListBulletIcon, EyeIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import { useToast } from '@/hooks/useToast';
import { CriteriaGroup, CriteriaBehavior, CriteriaIndicator } from '@/features/PerformanceCriteria/services/criteriaService';
import { getEvaluationScales, EvaluationScale } from '@/features/PerformanceCriteria/services/scalesService';
import { criteriaAIService } from '@/features/PerformanceCriteria/services/criteriaAIService';
import { getQuestionnaireById, saveQuestionnaire, Questionnaire } from '../services/questionnairesService';
// Removed QuestionnairePreview import

import MDEditor from '@uiw/react-md-editor';

// Simple helper for ID generation
const generateId = () => Math.random().toString(36).substr(2, 9);

export function QuestionnaireBuilderPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isNew = id === 'new';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [scales, setScales] = useState<EvaluationScale[]>([]);
    const [questionnaire, setQuestionnaire] = useState<Questionnaire>({
        title: '',
        description: '',
        type: 'PERFORMANCE',
        criteria: []
    });

    const [selectedGroup, setSelectedGroup] = useState<CriteriaGroup | null>(null);
    const [selectedPage, setSelectedPage] = useState<'INTRODUCTION' | 'CONCLUSION' | null>(null);
    const [editingAudience, setEditingAudience] = useState<'COLLABORATOR' | 'LEADER'>('COLLABORATOR');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const scalesData = await getEvaluationScales();
            setScales(scalesData);

            if (!isNew && id) {
                const data = await getQuestionnaireById(id);
                if (data) {
                    setQuestionnaire(data);
                    if (data.criteria.length > 0) {
                        setSelectedGroup(data.criteria[0]);
                    }
                }
            } else {
                // Initialize with one standard group if new
                const initialGroup: CriteriaGroup = {
                    id: uuidv4(),
                    description: 'Competências Gerais',
                    type: 'STANDARD',
                    behaviors: []
                };
                setQuestionnaire(prev => ({ ...prev, criteria: [initialGroup] }));
                setSelectedGroup(initialGroup);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error("Erro", "Erro ao carregar dados");
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // STATE HELPERS
    // ==========================================

    const updateQuestionnaire = (updates: Partial<Questionnaire>) => {
        setQuestionnaire(prev => ({ ...prev, ...updates }));
        setHasUnsavedChanges(true);
    };

    const updateCriteriaGroups = (newGroups: CriteriaGroup[]) => {
        setQuestionnaire(prev => ({ ...prev, criteria: newGroups }));

        // Update selected group reference if it exists
        if (selectedGroup) {
            const updated = newGroups.find(g => g.id === selectedGroup.id);
            if (updated) setSelectedGroup(updated);
        }
        setHasUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!questionnaire.title.trim()) {
            toast.error("Erro", "O título é obrigatório");
            return;
        }

        setSaving(true);
        try {
            const payload = { ...questionnaire };
            if (!isNew && id) {
                payload.id = id;
            }

            const savedId = await saveQuestionnaire(payload);
            setHasUnsavedChanges(false);
            toast.success("Sucesso", "Questionário salvo com sucesso!");

            if (isNew && savedId) {
                navigate(`/people/evaluations/questionnaires/${savedId}`, { replace: true });
                return savedId;
            }
            return id;
        } catch (error) {
            console.error('Error saving:', error);
            toast.error("Erro", "Erro ao salvar questionário");
            return undefined;
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async (view: 'COLLABORATOR' | 'LEADER') => {
        let currentId = id;
        if (hasUnsavedChanges || isNew) {
            // Must save first
            currentId = await handleSave();
            if (!currentId) return; // Save failed
        }

        if (currentId && currentId !== 'new') {
            navigate(`/collaborators/evaluations/preview?view=${view}&questionnaireId=${currentId}`);
        }
    };

    // ==========================================
    // CRITERIA FUNCTIONS
    // ==========================================

    const handleCreateGroup = (type: CriteriaGroup['type'] = 'STANDARD') => {
        const newGroup: CriteriaGroup = {
            id: generateId(),
            description: type === 'PROLOGUE' ? 'Prólogo' : (type === 'EPILOGUE' ? 'Epílogo' : 'Novo Grupo'),
            type,
            behaviors: []
        };
        const newGroups = [...questionnaire.criteria, newGroup];
        updateCriteriaGroups(newGroups);
        setSelectedGroup(newGroup);
        setSelectedPage(null);
    };

    const handleSelectGroup = (group: CriteriaGroup) => {
        setSelectedGroup(group);
    };

    const handleDeleteGroup = (groupId: string) => {
        if (!window.confirm("Excluir este grupo?")) return;
        const newGroups = questionnaire.criteria.filter(g => g.id !== groupId);
        updateCriteriaGroups(newGroups);
        if (selectedGroup?.id === groupId) {
            setSelectedGroup(null);
        }
    };

    const handleUpdateGroupDescription = (groupId: string, desc: string) => {
        const newGroups = questionnaire.criteria.map(g =>
            g.id === groupId ? { ...g, description: desc } : g
        );
        updateCriteriaGroups(newGroups);
    };

    // --- BEHAVIORS ---

    const addBehavior = () => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newBehavior: CriteriaBehavior = {
            id: generateId(),
            description: '',
            indicators: []
        };
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return { ...g, behaviors: [...g.behaviors, newBehavior] };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    const updateBehavior = (behaviorId: string, field: keyof CriteriaBehavior, value: any) => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return {
                    ...g,
                    behaviors: g.behaviors.map(b => b.id === behaviorId ? { ...b, [field]: value } : b)
                };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    const deleteBehavior = (behaviorId: string) => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return {
                    ...g,
                    behaviors: g.behaviors.filter(b => b.id !== behaviorId)
                };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    // --- INDICATORS ---

    const addIndicator = (behaviorId: string) => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newIndicator: CriteriaIndicator = {
            id: generateId(),
            description: '',
            isActive: true,
            selfEvaluationQuestion: '',
            leaderEvaluationQuestion: '',
            scaleId: scales.length > 0 ? scales[0].id : undefined
        };
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return {
                    ...g,
                    behaviors: g.behaviors.map(b => {
                        if (b.id === behaviorId) {
                            return { ...b, indicators: [...b.indicators, newIndicator] };
                        }
                        return b;
                    })
                };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    const updateIndicator = (behaviorId: string, indicatorId: string, field: keyof CriteriaIndicator, value: any) => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return {
                    ...g,
                    behaviors: g.behaviors.map(b => {
                        if (b.id === behaviorId) {
                            return {
                                ...b,
                                indicators: b.indicators.map(i => i.id === indicatorId ? { ...i, [field]: value } : i)
                            };
                        }
                        return b;
                    })
                };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    const deleteIndicator = (behaviorId: string, indicatorId: string) => {
        if (!selectedGroup || !selectedGroup.id) return;
        const newGroups = questionnaire.criteria.map(g => {
            if (g.id === selectedGroup.id) {
                return {
                    ...g,
                    behaviors: g.behaviors.map(b => {
                        if (b.id === behaviorId) {
                            return {
                                ...b,
                                indicators: b.indicators.filter(i => i.id !== indicatorId)
                            };
                        }
                        return b;
                    })
                };
            }
            return g;
        });
        updateCriteriaGroups(newGroups);
    };

    const handleGenerateQuestions = async (behaviorId: string, indicator: CriteriaIndicator) => {
        if (!selectedGroup || !selectedGroup.id) return;
        if (!indicator.description) {
            toast.error("Erro", "Preencha a descrição do indicador primeiro.");
            return;
        }

        const loader = toast.loading("IA", "Gerando perguntas...");

        try {
            const result = await criteriaAIService.generateIndicatorQuestions(indicator.description, scales);

            const newGroups = questionnaire.criteria.map(g => {
                if (g.id === selectedGroup.id) {
                    return {
                        ...g,
                        behaviors: g.behaviors.map(b => {
                            if (b.id === behaviorId) {
                                return {
                                    ...b,
                                    indicators: b.indicators.map(i => {
                                        if (i.id === indicator.id) {
                                            return {
                                                ...i,
                                                selfEvaluationQuestion: result.selfEvaluationQuestion,
                                                leaderEvaluationQuestion: result.leaderEvaluationQuestion,
                                                scaleId: (result.suggestedScaleId && scales.some(s => s.id === result.suggestedScaleId))
                                                    ? result.suggestedScaleId
                                                    : i.scaleId
                                            };
                                        }
                                        return i;
                                    })
                                };
                            }
                            return b;
                        })
                    };
                }
                return g;
            });

            updateCriteriaGroups(newGroups);
            toast.dismiss(loader);
            toast.success("Sucesso", "Perguntas geradas!");
        } catch (error) {
            console.error(error);
            toast.dismiss(loader);
            toast.error("Erro", "Não foi possível gerar.");
        }
    };

    if (loading) return <div className="p-8">Carregando...</div>;

    return (
        <div className="flex flex-col h-[calc(100vh-9rem)] gap-6">
            {/* HEADER CARD */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-5 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4 flex-1">
                    <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 transition-colors hover:bg-gray-50 p-2 rounded-full">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Título do Questionário"
                            className="block w-full border-0 border-b border-transparent bg-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-0 text-xl font-bold text-gray-900 placeholder-gray-300 px-0 transition-colors"
                            value={questionnaire.title}
                            onChange={(e) => updateQuestionnaire({ title: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Descrição opcional"
                            className="block w-full border-0 bg-transparent text-sm text-gray-500 placeholder-gray-300 focus:ring-0 mt-0.5 px-0"
                            value={questionnaire.description}
                            onChange={(e) => updateQuestionnaire({ description: e.target.value })}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 mr-2 border-r border-gray-200 pr-4">
                        <button
                            onClick={() => handlePreview('COLLABORATOR')}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Visualizar como Colaborador"
                        >
                            <EyeIcon className="h-4 w-4" />
                            <span className="hidden xl:inline">Visão Colaborador</span>
                        </button>
                        <button
                            onClick={() => handlePreview('LEADER')}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Visualizar como Líder"
                        >
                            <UserGroupIcon className="h-4 w-4" />
                            <span className="hidden xl:inline">Visão Líder</span>
                        </button>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-2 transition-colors shadow-sm ${hasUnsavedChanges
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                            }`}>
                            <span className={`w-2 h-2 rounded-full ${hasUnsavedChanges ? 'bg-amber-500' : 'bg-green-500'
                                }`} />
                            {hasUnsavedChanges ? 'Não salvo' : 'Salvo'}
                        </span>
                        <Button onClick={handleSave} disabled={!hasUnsavedChanges && !isNew}>
                            <ArrowDownOnSquareIcon className="h-4 w-4 mr-2" />
                            Salvar
                        </Button>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT CARD */}
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex overflow-hidden">
                {/* SIDEBAR: GROUPS & PAGES */}
                <div className="w-1/3 md:w-1/4 bg-gray-50/50 border-r border-gray-200 flex flex-col">
                    {/* PAGES SECTION */}
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Páginas</h2>
                        <div className="space-y-1">
                            <button
                                onClick={() => { setSelectedPage('INTRODUCTION'); setSelectedGroup(null); }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedPage === 'INTRODUCTION' ? 'bg-white text-indigo-700 shadow-sm border border-indigo-200' : 'text-gray-600 hover:bg-white hover:text-gray-900 border border-transparent'}`}
                            >
                                Introdução
                            </button>
                            <button
                                onClick={() => { setSelectedPage('CONCLUSION'); setSelectedGroup(null); }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedPage === 'CONCLUSION' ? 'bg-white text-indigo-700 shadow-sm border border-indigo-200' : 'text-gray-600 hover:bg-white hover:text-gray-900 border border-transparent'}`}
                            >
                                Conclusão
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Grupos</h2>
                            <button onClick={() => handleCreateGroup('STANDARD')} className="text-indigo-600 hover:text-indigo-800 p-1.5 bg-white shadow-sm border border-gray-200 rounded-lg hover:bg-indigo-50 transition-all">
                                <PlusIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex gap-2 text-[10px]">
                            {!questionnaire.criteria.some(g => g.type === 'PROLOGUE') && (
                                <button onClick={() => handleCreateGroup('PROLOGUE')} className="text-indigo-600 border border-indigo-200 px-2 py-1.5 rounded-lg bg-white hover:bg-indigo-50 shadow-sm transition-all flex-1">+ Prólogo</button>
                            )}
                            {!questionnaire.criteria.some(g => g.type === 'EPILOGUE') && (
                                <button onClick={() => handleCreateGroup('EPILOGUE')} className="text-indigo-600 border border-indigo-200 px-2 py-1.5 rounded-lg bg-white hover:bg-indigo-50 shadow-sm transition-all flex-1">+ Epílogo</button>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {questionnaire.criteria.map(group => (
                            <div
                                key={group.id}
                                onClick={() => { handleSelectGroup(group); setSelectedPage(null); }}
                                className={`cursor-pointer px-4 py-3 rounded-xl flex justify-between items-center group transition-all ${selectedGroup?.id === group.id ? 'bg-white border-indigo-200 border-2 shadow-sm text-indigo-700' : 'hover:bg-white border border-transparent hover:border-gray-200 text-gray-600'}`}
                            >
                                <div>
                                    <span className="font-semibold text-sm block mb-0.5">{group.description || '(Sem nome)'}</span>
                                    {group.type && group.type !== 'STANDARD' && (
                                        <span className="text-[10px] text-gray-400 uppercase tracking-wide bg-gray-100 px-1.5 py-0.5 rounded">{group.type}</span>
                                    )}
                                </div>
                                {group.type === 'STANDARD' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id!); }}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                        {questionnaire.criteria.length === 0 && (
                            <div className="text-center py-12 px-4">
                                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6">
                                    <p className="text-gray-400 text-sm">Nenhum grupo criado.</p>
                                    <p className="text-gray-300 text-xs mt-1">Clique no + para começar</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* MAIN EDITOR */}
                <div className="flex-1 flex flex-col overflow-hidden bg-white">
                    {selectedPage ? (
                        <div className="flex-1 flex flex-col h-full">
                            <div className="px-8 py-6 border-b border-gray-100 z-10">
                                <label className="block text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">Editando {selectedPage === 'INTRODUCTION' ? 'Introdução' : 'Conclusão'}</label>
                                <div className="flex justify-between items-start">
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {selectedPage === 'INTRODUCTION' ? 'Texto de Boas-vindas' : 'Mensagem de Encerramento'}
                                    </h2>

                                    {/* AUDIENCE TOGGLE */}
                                    <div className="flex bg-gray-100 p-1 rounded-lg">
                                        <button
                                            onClick={() => setEditingAudience('COLLABORATOR')}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${editingAudience === 'COLLABORATOR' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            Para o Colaborador
                                        </button>
                                        <button
                                            onClick={() => setEditingAudience('LEADER')}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${editingAudience === 'LEADER' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            Para o Líder
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedPage === 'INTRODUCTION'
                                        ? 'Este texto será exibido antes da primeira pergunta.'
                                        : 'Este texto será exibido após a finalização do questionário.'}
                                </p>
                            </div>
                            <div className="flex-1 p-8 bg-gray-50 overflow-hidden flex flex-col">
                                <MDEditor
                                    value={
                                        selectedPage === 'INTRODUCTION'
                                            ? (editingAudience === 'COLLABORATOR' ? (questionnaire.introductionCollaborator || '') : (questionnaire.introductionLeader || ''))
                                            : (editingAudience === 'COLLABORATOR' ? (questionnaire.conclusionCollaborator || '') : (questionnaire.conclusionLeader || ''))
                                    }
                                    onChange={(val) => {
                                        if (selectedPage === 'INTRODUCTION') {
                                            if (editingAudience === 'COLLABORATOR') updateQuestionnaire({ introductionCollaborator: val });
                                            else updateQuestionnaire({ introductionLeader: val });
                                        } else {
                                            if (editingAudience === 'COLLABORATOR') updateQuestionnaire({ conclusionCollaborator: val });
                                            else updateQuestionnaire({ conclusionLeader: val });
                                        }
                                    }}
                                    height="100%"
                                    className="flex-1 shadow-sm border border-gray-200 rounded-xl overflow-hidden"
                                    preview="edit"
                                />
                            </div>
                        </div>
                    ) : selectedGroup ? (
                        <>
                            <div className="px-8 py-6 border-b border-gray-100 z-10">
                                <label className="block text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">Editando Grupo</label>
                                <input
                                    type="text"
                                    className="block w-full border-0 border-b-2 border-gray-100 focus:border-indigo-500 p-0 text-xl font-bold text-gray-900 placeholder-gray-300 transition-colors bg-transparent focus:ring-0"
                                    value={selectedGroup.description}
                                    onChange={(e) => handleUpdateGroupDescription(selectedGroup.id!, e.target.value)}
                                    placeholder="Nome do Grupo (ex: Competências Técnicas)"
                                />
                            </div>

                            <div className="flex-1 overflow-y-auto p-8 bg-white">
                                <div className="flex justify-between items-center mb-8">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Comportamentos</h3>
                                        <p className="text-sm text-gray-500">Defina os comportamentos esperados e seus indicadores.</p>
                                    </div>
                                    <Button size="sm" variant="secondary" onClick={addBehavior}>
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        Adicionar
                                    </Button>
                                </div>

                                <div className="space-y-8">
                                    {selectedGroup.behaviors.map((behavior) => (
                                        <div key={behavior.id} className="bg-gray-50 border border-gray-200 rounded-2xl shadow-sm overflow-hidden group/behavior transition-all hover:border-gray-300">
                                            {/* Behavior Header */}
                                            <div className="p-5 border-b border-gray-200 flex items-start gap-4">
                                                <div className="flex-1">
                                                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Descrição do Comportamento</label>
                                                    <input
                                                        type="text"
                                                        className="block w-full border-0 bg-transparent p-0 focus:ring-0 text-lg font-medium text-gray-900 placeholder-gray-400"
                                                        placeholder="Digite a descrição do comportamento..."
                                                        value={behavior.description}
                                                        onChange={(e) => updateBehavior(behavior.id, 'description', e.target.value)}
                                                    />
                                                </div>
                                                <button onClick={() => deleteBehavior(behavior.id)} className="text-gray-400 hover:text-red-500 hover:bg-white p-2 rounded-lg transition-all opacity-0 group-hover/behavior:opacity-100">
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </div>

                                            {/* Indicators */}
                                            <div className="p-5 space-y-4">
                                                {behavior.indicators.map((indicator) => (
                                                    <div key={indicator.id} className="p-5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-indigo-200 transition-all">
                                                        <div className="flex items-start gap-4 mb-5">
                                                            <button
                                                                onClick={() => updateIndicator(behavior.id, indicator.id, 'isActive', !indicator.isActive)}
                                                                className={`mt-1 flex-shrink-0 transition-colors ${indicator.isActive ? 'text-green-500 hover:text-green-600' : 'text-gray-300 hover:text-gray-400'}`}
                                                            >
                                                                <CheckCircleIcon className="h-6 w-6" />
                                                            </button>
                                                            <div className="flex-1 space-y-4">
                                                                <div className="flex gap-4">
                                                                    <div className="flex-1">
                                                                        <label className="block text-xs font-medium text-gray-400 mb-1">Indicador</label>
                                                                        <input
                                                                            type="text"
                                                                            className="block w-full border-gray-200 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-50/50"
                                                                            placeholder="Descreva o indicador..."
                                                                            value={indicator.description}
                                                                            onChange={(e) => updateIndicator(behavior.id, indicator.id, 'description', e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="w-48">
                                                                        <label className="block text-xs font-medium text-gray-400 mb-1">Escala de Avaliação</label>
                                                                        <select
                                                                            className="block w-full rounded-lg border-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50/50"
                                                                            value={indicator.scaleId || ''}
                                                                            onChange={(e) => updateIndicator(behavior.id, indicator.id, 'scaleId', e.target.value)}
                                                                        >
                                                                            <option value="" disabled>Selecione...</option>
                                                                            {scales.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                                        </select>
                                                                    </div>
                                                                    {/* Actions */}
                                                                    <div className="flex items-end gap-1 pb-0.5">
                                                                        <button onClick={() => handleGenerateQuestions(behavior.id, indicator)} className="text-indigo-600 hover:text-indigo-800 p-2 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors" title="Gerar perguntas com IA">
                                                                            <SparklesIcon className="h-5 w-5" />
                                                                        </button>
                                                                        <button onClick={() => deleteIndicator(behavior.id, indicator.id)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                                                            <XMarkIcon className="h-5 w-5" />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Questions */}
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                                                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Pergunta de Autoavaliação</span>
                                                                        </div>
                                                                        <textarea
                                                                            rows={3}
                                                                            className="block w-full border-gray-200 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none bg-white"
                                                                            value={indicator.selfEvaluationQuestion || ''}
                                                                            onChange={(e) => updateIndicator(behavior.id, indicator.id, 'selfEvaluationQuestion', e.target.value)}
                                                                            placeholder="Pergunta exibida para o colaborador..."
                                                                        />
                                                                    </div>
                                                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                                                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Pergunta do Líder</span>
                                                                        </div>
                                                                        <textarea
                                                                            rows={3}
                                                                            className="block w-full border-gray-200 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none bg-white"
                                                                            value={indicator.leaderEvaluationQuestion || ''}
                                                                            onChange={(e) => updateIndicator(behavior.id, indicator.id, 'leaderEvaluationQuestion', e.target.value)}
                                                                            placeholder="Pergunta exibida para o líder..."
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                <button onClick={() => addIndicator(behavior.id)} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-500 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/50 transition-all flex items-center justify-center gap-2">
                                                    <PlusIcon className="h-4 w-4" />
                                                    Adicionar Novo Indicador
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {selectedGroup.behaviors.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-20 px-4 text-center border-2 border-dashed border-gray-200 rounded-3xl bg-gray-50">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                <SparklesIcon className="h-8 w-8 text-gray-300" />
                                            </div>
                                            <h3 className="text-lg font-medium text-gray-900 mb-1">Comece adicionando comportamentos</h3>
                                            <p className="text-gray-500 max-w-sm mb-6">Cada grupo precisa de pelo menos um comportamento para ser avaliado.</p>
                                            <Button variant="secondary" onClick={addBehavior}>
                                                <PlusIcon className="h-4 w-4 mr-2" />
                                                Adicionar Primeiro Comportamento
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50/50">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                                <ListBulletIcon className="h-10 w-10 text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum grupo selecionado</h3>
                            <p className="text-gray-500 max-w-md text-center">Selecione um grupo na barra lateral ou crie um novo para começar a editar os critérios.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* PREVIEW MODAL */}

        </div>
    );
}
